import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface Organization {
  id: number;
  name: string;
  slug: string;
}

interface Institution {
  id: number;
  name: string;
  slug: string;
  role: string;
}

interface User {
  id: number;
  google_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  profile_picture?: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
  is_active: boolean;
  email_verified: boolean;
  user_type: string;
  user_role: string;
  organization_id?: number;
  organization_name?: string;
}

interface Student {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  grade_level?: string;
  reading_level?: number;
  organization_id: number;
  institution_id: number;
  class_count?: number;
  assignment_count?: number;
  classes?: any[];
}

interface AuthContextType {
  user: User | null;
  student: Student | null;
  token: string | null;
  tokenExpiration: number | null;
  organization: Organization | null;
  institutions: Institution[];
  currentInstitution: Institution | null;
  setCurrentInstitution: (institution: Institution | null) => void;
  login: (credentialResponse: any) => Promise<void>;
  studentLogin: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  isStudent: boolean;
  isGlobalAdmin: boolean;
  isOrgAdmin: boolean;
  isInstitutionAdmin: boolean;
  isTeacher: boolean;
  isImpersonating: boolean;
  impersonatorName: string | null;
  impersonateUser: (userId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to parse JWT and get expiration time
const parseJwtExpiration = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp) {
      // exp is in seconds, convert to milliseconds
      return payload.exp * 1000;
    }
    return null;
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [student, setStudent] = useState<Student | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tokenExpiration, setTokenExpiration] = useState<number | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [currentInstitution, setCurrentInstitution] = useState<Institution | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatorName, setImpersonatorName] = useState<string | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user from localStorage on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('auth_user');
        const storedStudent = localStorage.getItem('auth_student');
        const userType = localStorage.getItem('user_type');

        if (storedToken && storedStudent && userType === 'student') {
          // Student login
          const expiration = parseJwtExpiration(storedToken);
          // Check if token is expired
          if (expiration && expiration <= Date.now()) {
            console.warn('Stored token is expired, logging out');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_student');
            localStorage.removeItem('user_type');
          } else {
            setToken(storedToken);
            setTokenExpiration(expiration);
            setStudent(JSON.parse(storedStudent));
          }
        } else if (storedToken && storedUser) {
          const expiration = parseJwtExpiration(storedToken);
          // Check if token is expired
          if (expiration && expiration <= Date.now()) {
            console.warn('Stored token is expired, logging out');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
          } else {
            setToken(storedToken);
            setTokenExpiration(expiration);
            setUser(JSON.parse(storedUser));
          
          // Verify token is still valid
          try {
            const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            
            if (response.data.user) {
              setUser(response.data.user);
              localStorage.setItem('auth_user', JSON.stringify(response.data.user));
              
              // Set organization and institutions
              if (response.data.organization) {
                setOrganization(response.data.organization);
                localStorage.setItem('auth_organization', JSON.stringify(response.data.organization));
              }
              
              if (response.data.institutions) {
                setInstitutions(response.data.institutions);
                localStorage.setItem('auth_institutions', JSON.stringify(response.data.institutions));
                
                // Set current institution if only one
                if (response.data.institutions.length === 1) {
                  setCurrentInstitution(response.data.institutions[0]);
                  localStorage.setItem('current_institution', JSON.stringify(response.data.institutions[0]));
                } else {
                  // Try to load previously selected institution
                  const storedInstitution = localStorage.getItem('current_institution');
                  if (storedInstitution) {
                    const parsed = JSON.parse(storedInstitution);
                    // Verify it's still valid
                    const stillValid = response.data.institutions.find((i: Institution) => i.id === parsed.id);
                    if (stillValid) {
                      setCurrentInstitution(parsed);
                    }
                  }
                }
              }
            }
          } catch (error) {
            // Token is invalid, clear auth data
            console.warn('Stored token is invalid, logging out');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            setToken(null);
            setTokenExpiration(null);
            setUser(null);
          }
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [API_BASE_URL]);

  const login = async (credentialResponse: any): Promise<void> => {
    try {
      setIsLoading(true);
      
      const response = await axios.post(`${API_BASE_URL}/api/auth/google-login`, {
        credential: credentialResponse.credential
      });

      const { access_token, user: userData, message } = response.data;
      
      // Store auth data
      const expiration = parseJwtExpiration(access_token);
      setToken(access_token);
      setTokenExpiration(expiration);
      setUser(userData);
      setStudent(null);
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      localStorage.setItem('user_type', 'teacher');
      localStorage.removeItem('auth_student');
      
      // Fetch full profile (org + institutions) immediately so the UI can route/setup correctly
      try {
        const meResponse = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        if (meResponse.data?.organization) {
          setOrganization(meResponse.data.organization);
          localStorage.setItem('auth_organization', JSON.stringify(meResponse.data.organization));
        } else {
          setOrganization(null);
          localStorage.removeItem('auth_organization');
        }
        if (meResponse.data?.institutions) {
          setInstitutions(meResponse.data.institutions);
          localStorage.setItem('auth_institutions', JSON.stringify(meResponse.data.institutions));
          if (meResponse.data.institutions.length === 1) {
            setCurrentInstitution(meResponse.data.institutions[0]);
            localStorage.setItem('current_institution', JSON.stringify(meResponse.data.institutions[0]));
          }
        } else {
          setInstitutions([]);
          localStorage.removeItem('auth_institutions');
        }
      } catch (e) {
        // Non-fatal: user is logged in; pages can still load what they need later.
        console.warn('Failed to fetch /auth/me after login:', e);
      }

      console.log('Login successful:', message);
      
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Clear any existing auth data
      logout();
      
      // Handle specific error types
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else {
        throw new Error('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const studentLogin = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      const response = await axios.post(`${API_BASE_URL}/api/auth/student-login`, {
        email,
        password
      });

      const { access_token, student: studentData, message } = response.data;
      
      // Store auth data
      const expiration = parseJwtExpiration(access_token);
      setToken(access_token);
      setTokenExpiration(expiration);
      setStudent(studentData);
      setUser(null);
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_student', JSON.stringify(studentData));
      localStorage.setItem('user_type', 'student');
      localStorage.removeItem('auth_user');
      
      console.log('Student login successful:', message);
      
    } catch (error: any) {
      console.error('Student login error:', error);
      
      // Clear any existing auth data
      logout();
      
      // Handle specific error types
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else {
        throw new Error('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const impersonateUser = async (userId: number): Promise<void> => {
    try {
      setIsLoading(true);
      
      const response = await axios.post(`${API_BASE_URL}/api/admin/impersonate/${userId}`);
      
      const { impersonation_token, impersonated_user, message } = response.data;
      
      // Decode JWT to get impersonator info
      const jwtPayload = JSON.parse(atob(impersonation_token.split('.')[1]));
      
      // Store original token for later
      if (token) {
        sessionStorage.setItem('original_token', token);
        sessionStorage.setItem('original_user', JSON.stringify(user));
      }
      
      // Switch to impersonation token
      const expiration = parseJwtExpiration(impersonation_token);
      setToken(impersonation_token);
      setTokenExpiration(expiration);
      setUser(impersonated_user);
      setIsImpersonating(true);
      setImpersonatorName(jwtPayload.impersonator_name);
      
      localStorage.setItem('auth_token', impersonation_token);
      localStorage.setItem('auth_user', JSON.stringify(impersonated_user));
      sessionStorage.setItem('is_impersonating', 'true');
      sessionStorage.setItem('impersonator_name', jwtPayload.impersonator_name);
      
      console.log('Impersonation started:', message);
      
      // Reload to fetch impersonated user's data
      window.location.reload();
      
    } catch (error: any) {
      console.error('Impersonation error:', error);
      throw new Error(error.response?.data?.error || 'Failed to impersonate user');
    } finally {
      setIsLoading(false);
    }
  };

  const stopImpersonation = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      const response = await axios.post(`${API_BASE_URL}/api/admin/stop-impersonation`);
      
      const { original_token, original_user, message } = response.data;
      
      // Restore original token
      const expiration = parseJwtExpiration(original_token);
      setToken(original_token);
      setTokenExpiration(expiration);
      setUser(original_user);
      setIsImpersonating(false);
      setImpersonatorName(null);
      
      localStorage.setItem('auth_token', original_token);
      localStorage.setItem('auth_user', JSON.stringify(original_user));
      sessionStorage.removeItem('is_impersonating');
      sessionStorage.removeItem('impersonator_name');
      sessionStorage.removeItem('original_token');
      sessionStorage.removeItem('original_user');
      
      console.log('Impersonation stopped:', message);
      
      // Reload to restore original user's data
      window.location.reload();
      
    } catch (error: any) {
      console.error('Stop impersonation error:', error);
      throw new Error(error.response?.data?.error || 'Failed to stop impersonation');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    // Clear auth state
    setUser(null);
    setStudent(null);
    setToken(null);
    setTokenExpiration(null);
    setOrganization(null);
    setInstitutions([]);
    setCurrentInstitution(null);
    setIsImpersonating(false);
    setImpersonatorName(null);
    
    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_student');
    localStorage.removeItem('user_type');
    localStorage.removeItem('auth_organization');
    localStorage.removeItem('auth_institutions');
    localStorage.removeItem('current_institution');
    
    // Clear sessionStorage
    sessionStorage.removeItem('is_impersonating');
    sessionStorage.removeItem('impersonator_name');
    sessionStorage.removeItem('original_token');
    sessionStorage.removeItem('original_user');
    
    // Clear axios default header
    delete axios.defaults.headers.common['Authorization'];
    
    console.log('Logout successful');
  };

  const refreshToken = async (): Promise<void> => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`);
      
      const { access_token } = response.data;
      
      // Update token and expiration
      const expiration = parseJwtExpiration(access_token);
      setToken(access_token);
      setTokenExpiration(expiration);
      localStorage.setItem('auth_token', access_token);
      
      console.log('Token refreshed successfully');
    } catch (error: any) {
      console.error('Token refresh error:', error);
      
      // If refresh fails, logout the user
      if (error.response?.status === 401) {
        logout();
      }
      
      throw new Error(error.response?.data?.error || 'Failed to refresh session');
    }
  };

  // Check for impersonation on load
  useEffect(() => {
    const impersonating = sessionStorage.getItem('is_impersonating') === 'true';
    const storedImpersonatorName = sessionStorage.getItem('impersonator_name');
    
    if (impersonating) {
      setIsImpersonating(true);
      setImpersonatorName(storedImpersonatorName);
    }
  }, []);

  const value: AuthContextType = {
    user,
    student,
    token,
    tokenExpiration,
    organization,
    institutions,
    currentInstitution,
    setCurrentInstitution: (institution: Institution | null) => {
      setCurrentInstitution(institution);
      if (institution) {
        localStorage.setItem('current_institution', JSON.stringify(institution));
      } else {
        localStorage.removeItem('current_institution');
      }
    },
    login,
    studentLogin,
    logout,
    refreshToken,
    isLoading,
    isAuthenticated: (!!user || !!student) && !!token,
    isStudent: !!student && !user,
    isGlobalAdmin: user?.user_role === 'global_admin',
    isOrgAdmin: user?.user_role === 'org_admin',
    isInstitutionAdmin: user?.user_role === 'institution_admin',
    isTeacher: user?.user_role === 'teacher',
    isImpersonating,
    impersonatorName,
    impersonateUser,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
